import React, { useState, useEffect } from 'react';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, Stack, TextField, MenuItem, FormControl, InputLabel, Select, Typography, Chip, Checkbox, ListItemText, Avatar, CircularProgress, Tooltip
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import WorkerPlanManager from './WorkerPlanManager';
import { Program } from '../services/programService';
import { Worker } from '../services/workerService';
import { Region } from '../services/regionService';
import { Settlement, listSettlements } from '../services/settlementService';
import { PlanWorkerCreate, updatePlanProgramStore } from '../services/planService';
// ;
import InfoIcon from '@mui/icons-material/Info';
import dayjs from "dayjs";

// Types
export type PlanFormValues = {
  name: string;
  month?: string; // Optional since backend doesn't require it
  start_date: string;
  days: number;
  region_id?: number;
  settlement_id?: number;
  program_ids?: number[];
  worker_ids: number[];
  plan_workers?: PlanWorkerCreate[]; // Add complete worker data
  yandex_options?: any;
};

interface PlanFormDialogProps {
  mode: 'create' | 'edit';
  open: boolean;
  onClose: () => void;
  plan?: any;
  programs: Program[];
  workers: Worker[];
  regions: Region[];
  settlements: Settlement[];
  onSubmit: (values: PlanFormValues) => void;
  isLoading?: boolean;
}

// Editable row component for program store
const EditableProgramStoreRow = ({ planId, pps}: { planId: number, pps: any }) => {
  const [visitTime, setVisitTime] = React.useState(pps.visit_time_minutes_override ?? pps.original_visit_time_minutes ?? '');
  const [rate, setRate] = React.useState(pps.rate_override ?? pps.original_rate ?? '');
  const [loading, setLoading] = React.useState(false);
  const [success, setSuccess] = React.useState(false);
  const [_error, setError] = React.useState<string | null>(null);
  const [jsonDialogOpen, setJsonDialogOpen] = React.useState(false);
  const [jsonValue, setJsonValue] = React.useState(() => JSON.stringify(pps.visiting_hours_override || pps.original_visiting_hours, null, 2));
  const [jsonError, setJsonError] = React.useState<string | null>(null);
  const [jsonLoading, setJsonLoading] = React.useState(false);
  const [jsonSuccess, setJsonSuccess] = React.useState(false);
  const [visitsCount, setVisitsCount] = React.useState(pps.visits_count ?? '');

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    try {
      await updatePlanProgramStore(planId, pps.id, {
        visit_time_minutes_override: visitTime === '' ? undefined : Number(visitTime),
        rate_override: rate === '' ? undefined : Number(rate),
        visit_time_minutes_modified: true,
        rate_modified: true,
      });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 1500);
    } catch (e: any) {
      setError(e?.message || 'Update failed');
    } finally {
      setLoading(false);
    }
  };

  const handleJsonSave = async () => {
    setJsonLoading(true);
    setJsonError(null);
    try {
      const parsed = JSON.parse(jsonValue);
      await updatePlanProgramStore(planId, pps.id, {
        visiting_hours_override: parsed,
        visiting_hours_modified: true,
      });
      setJsonSuccess(true);
      setTimeout(() => setJsonSuccess(false), 1500);
      setJsonDialogOpen(false);
    } catch (e: any) {
      setJsonError(e?.message || 'Invalid JSON or update failed');
    } finally {
      setJsonLoading(false);
    }
  };

  return (
    <tr>
      <td style={{ padding: 8 }}>{pps.program_name}</td>
      <td style={{ padding: 8 }}>
        {pps.store_name}
        <div style={{ fontSize: 12, color: '#888' }}>{pps.store_address}</div>
      </td>
      <td style={{ padding: 8 }}>
        <TextField
          size="small"
          type="number"
          value={visitTime}
          onChange={e => setVisitTime(e.target.value)}
          sx={{ width: 80 }}
        />
      </td>
      <td style={{ padding: 8 }}>
        <TextField
          size="small"
          type="number"
          inputProps={{ step: 0.01 }}
          value={rate}
          onChange={e => setRate(e.target.value)}
          sx={{ width: 100 }}
        />
      </td>
      <td style={{ padding: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
        <TextField
          size="small"
          type="number"
          value={visitsCount}
          onChange={e => setVisitsCount(e.target.value)}
          sx={{ width: 80 }}
          label="Visits Count"
        />
        <Tooltip title={<pre style={{ margin: 0 }}>{JSON.stringify(pps.visiting_hours_override || pps.original_visiting_hours, null, 2)}</pre>}>
          <InfoIcon color="action" style={{ cursor: 'pointer' }} />
        </Tooltip>
      </td>
      <td style={{ padding: 8 }}>
        <Button
          variant="outlined"
          size="small"
          onClick={() => setJsonDialogOpen(true)}
        >
          Edit Visiting Hours
        </Button>
        <Dialog open={jsonDialogOpen} onClose={() => setJsonDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Visiting Hours JSON</DialogTitle>
          <DialogContent>
            <TextField
              multiline
              minRows={8}
              maxRows={16}
              fullWidth
              value={jsonValue}
              onChange={e => setJsonValue(e.target.value)}
              label="visiting_hours_override (JSON)"
              variant="outlined"
              error={!!jsonError}
              helperText={jsonError || 'Edit the JSON structure for visiting_hours_override.'}
              sx={{ fontFamily: 'monospace', mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setJsonDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleJsonSave}
              disabled={jsonLoading}
            >
              {jsonLoading ? <CircularProgress size={20} /> : jsonSuccess ? 'Saved!' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>
      </td>
      <td style={{ padding: 8 }}>
        <Button
          variant="contained"
          size="small"
          onClick={handleSave}
          disabled={loading}
        >
          {loading ? <CircularProgress size={20} /> : success ? 'Saved!' : 'Save'}
        </Button>
        {/*{error && <div style={{ color: 'red', fontSize: 12 }}>{error}</div>}*/}
      </td>
    </tr>
  );
};

const PlanFormDialog: React.FC<PlanFormDialogProps> = ({
  mode,
  open,
  onClose,
  plan,
  programs,
  workers,
  regions,
  settlements,
  onSubmit,
  isLoading = false
}) => {
  const [workerManagerOpen, setWorkerManagerOpen] = useState(false);
  const [formRegionId, setFormRegionId] = useState<number | ''>('');
  const [, setFormSettlementId] = useState<number | ''>('');
  const [workerManagerRegionId, setWorkerManagerRegionId] = useState<number | ''>('');
  const [workerManagerSettlementId, setWorkerManagerSettlementId] = useState<number | ''>('');

  const form = useForm<PlanFormValues>({
    defaultValues: {
      name: '',
      month: '',
      start_date: dayjs().format('YYYY-MM-DD'),
      days: 30,
      region_id: undefined,
      settlement_id: undefined,
      program_ids: [],
      worker_ids: [],
      plan_workers: [], // Initialize plan_workers
      yandex_options: { date: dayjs().format('YYYY-MM-DD'), planning_days: 30 },
    }
  });

  const getInitialValues = (): PlanFormValues => {
    if (mode === 'edit' && plan) {
      let yandexOptions = plan?.yandex_options || {};
      // Default start date: first of month, default days: 30
      let defaultDate = plan?.month ? `${plan.month}-01` : dayjs().format('YYYY-MM-DD');
      let defaultDays = 30;
      
      // Use plan.start_date and plan.days as primary values, fallback to yandex_options
      const startDate = plan.start_date ? dayjs(plan.start_date).format('YYYY-MM-DD') : (yandexOptions.date || defaultDate);
      const days = plan.days || yandexOptions.planning_days || defaultDays;
      
      return {
        name: plan.name || '',
        month: plan.month || '',
        start_date: startDate,
        days: days,
        region_id: plan.region_id,
        settlement_id: plan.settlement_id,
        program_ids: plan.plan_program_stores?.map((pps: any) => pps.program_store?.program_id).filter(Boolean) || [],
        worker_ids: plan.plan_workers?.map((pw: any) => pw.worker_id).filter(Boolean) || [],
        plan_workers: plan.plan_workers?.map((pw: any) => ({
          worker_id: pw.worker_id,
          availability_hours_override: pw.availability_hours_override,
          pricing_override: pw.pricing_override,
          work_time_override: pw.work_time_override,
          default_timings_override: pw.default_timings_override,
          availability_hours_modified: pw.availability_hours_modified,
          pricing_modified: pw.pricing_modified,
          work_time_modified: pw.work_time_modified,
          default_timings_modified: pw.default_timings_modified,
        })) || [],
        yandex_options: {
          ...yandexOptions,
          date: startDate,
          planning_days: days,
        },
      };
    }
    return {
      name: '',
      month: '',
      start_date: dayjs().format('YYYY-MM-DD'),
      days: 30,
      region_id: undefined,
      settlement_id: undefined,
      program_ids: [],
      worker_ids: [],
      plan_workers: [],
      yandex_options: { date: dayjs().format('YYYY-MM-DD'), planning_days: 30 },
    };
  };

  // Generate month options
  const generateMonthOptions = () => {
    const options = [];
    const currentDate = new Date();
    for (let i = -6; i <= 12; i++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() + i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
      options.push({ value, label });
    }
    return options;
  };

  const monthOptions = generateMonthOptions();

  // Filter settlements based on selected region
  const { data: formSettlements = [] } = useQuery<Settlement[]>({
    queryKey: ['settlements', formRegionId],
    queryFn: () => listSettlements(formRegionId || undefined),
    enabled: !!formRegionId
  });

  // Helper to get number of days in a month
  const getDaysInMonth = (monthStr: string) => {
    if (!monthStr) return 30;
    const [year, month] = monthStr.split('-').map(Number);
    if (!year || !month) return 30;
    return dayjs(`${year}-${String(month).padStart(2, '0')}-01`).daysInMonth();
  };

  // Watch for month changes and update planning period accordingly
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'month' && value.month) {
        const firstDay = `${value.month}-01`;
        const days = getDaysInMonth(value.month);
        form.setValue('start_date', firstDay);
        form.setValue('days', days);
        // Also update yandex_options for backward compatibility
        form.setValue('yandex_options.date', firstDay);
        form.setValue('yandex_options.planning_days', days);
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

  // Watch for start_date and days changes and sync to yandex_options
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'start_date' && value.start_date) {
        form.setValue('yandex_options.date', value.start_date);
      }
      if (name === 'days' && value.days) {
        form.setValue('yandex_options.planning_days', value.days);
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

  // Reset form when dialog opens/closes or plan changes
  useEffect(() => {
    if (open) {
      const initialValues = getInitialValues();
      form.reset(initialValues);
      setFormRegionId(initialValues.region_id || '');
      setFormSettlementId(initialValues.settlement_id || '');
      setWorkerManagerRegionId(initialValues.region_id || '');
      setWorkerManagerSettlementId(initialValues.settlement_id || '');
    } else {
      setFormRegionId('');
      setFormSettlementId('');
      setWorkerManagerRegionId('');
      setWorkerManagerSettlementId('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, plan, mode]);

  const handleSubmit = (values: any) => {
    if (mode === 'create') {
      if (!values.name || !values.start_date || !values.days || !values.program_ids?.length || !values.worker_ids?.length) {
        return;
      }
    } else {
      if (!values.name || !values.start_date || !values.days) {
        return;
      }
    }
    onSubmit(values);
  };

  // Handler for complete worker data changes
  const handlePlanWorkersChange = (planWorkers: PlanWorkerCreate[]) => {
    form.setValue('plan_workers', planWorkers);
  };

  const watchedWorkerIds = form.watch('worker_ids') || [];
  const selectedWorkers = workers.filter(w => w.id && watchedWorkerIds.includes(w.id));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <DialogTitle>
          {mode === 'create' ? 'Create New Plan' : `Edit Plan: ${plan?.name}`}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <Controller
              name="name"
              control={form.control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="Plan Name"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  fullWidth
                />
              )}
            />

            <Controller
              name="month"
              control={form.control}
              render={({ field, fieldState }) => (
                <FormControl fullWidth error={!!fieldState.error} >
                  <InputLabel>Month</InputLabel>
                  <Select {...field} label="Month">
                    {monthOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {fieldState.error && (
                    <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
                      {fieldState.error.message}
                    </Typography>
                  )}
                </FormControl>
              )}
            />

            <Controller
              name="start_date"
              control={form.control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="Planning Start Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  fullWidth
                  sx={{ mb: 2 }}
                />
              )}
            />
            <Controller
              name="days"
              control={form.control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="Planning Days"
                  type="number"
                  inputProps={{ min: 1, max: 365 }}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  fullWidth
                  sx={{ mb: 2 }}
                />
              )}
            />

            <Controller
              name="region_id"
              control={form.control}
              render={({ field, fieldState }) => (
                <FormControl fullWidth error={!!fieldState.error}>
                  <InputLabel>Region</InputLabel>
                  <Select
                    {...field}
                    label="Region"
                    value={field.value || ''}
                    onChange={(e) => {
                      const newRegionId = e.target.value ? Number(e.target.value) : undefined;
                      field.onChange(newRegionId);                      
                      setFormRegionId(newRegionId || '');
                      setWorkerManagerRegionId(newRegionId || '');
                      form.setValue('settlement_id', undefined);
                      setFormSettlementId('');
                    }}
                    disabled={regions.length === 0}
                  >
                    <MenuItem value="">None</MenuItem>
                    {regions.map((region) => (
                      <MenuItem key={region.id} value={region.id}>
                        {region.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {fieldState.error && (
                    <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
                      {fieldState.error.message}
                    </Typography>
                  )}
                </FormControl>
              )}
            />

            <Controller
              name="settlement_id"
              control={form.control}
              render={({ field, fieldState }) => (
                <FormControl fullWidth error={!!fieldState.error}>
                  <InputLabel>Settlement</InputLabel>
                  <Select
                    {...field}
                    label="Settlement"
                    value={field.value || ''}
                    onChange={(e) => {
                      const newSettlementId = e.target.value ? Number(e.target.value) : undefined;
                      field.onChange(newSettlementId);
                      setFormSettlementId(newSettlementId || '');
                      setWorkerManagerSettlementId(newSettlementId || '');
                    }}
                    disabled={formSettlements.length === 0}
                  >
                    <MenuItem value="">None</MenuItem>
                    {formSettlements.map((settlement) => (
                      <MenuItem key={settlement.id} value={settlement.id}>
                        {settlement.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {fieldState.error && (
                    <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
                      {fieldState.error.message}
                    </Typography>
                  )}
                </FormControl>
              )}
            />

            {mode === 'create' && (
              <Controller
                name="program_ids"
                control={form.control}
                render={({ field, fieldState }) => (
                  <FormControl fullWidth error={!!fieldState.error}>
                    <InputLabel>Programs</InputLabel>
                    <Select
                      {...field}
                      multiple
                      label="Programs"
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(selected as number[]).map((value) => {
                            const program = programs.find(p => p.id === value);
                            return (
                              <Chip key={value} label={program?.name || `Program ${value}`} size="small" />
                            );
                          })}
                        </Box>
                      )}
                    >
                      {programs.map((program) => (
                        <MenuItem key={program.id} value={program.id}>
                          <Checkbox checked={(field.value || []).indexOf(program.id!) > -1} />
                          <ListItemText primary={program.name} />
                        </MenuItem>
                      ))}
                    </Select>
                    {fieldState.error && (
                      <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
                        {fieldState.error.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />
            )}

            <Controller
              name="worker_ids"
              control={form.control}
              render={({ field, fieldState }) => (
                <FormControl fullWidth error={!!fieldState.error}>
                  <Box>
                    <Typography variant="subtitle1" gutterBottom>
                      Workers Management
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={() => setWorkerManagerOpen(true)}
                      fullWidth
                      sx={{ mb: 2, py: 2 }}
                    >
                      Manage Workers ({selectedWorkers.length} selected)
                    </Button>
                    {selectedWorkers.length > 0 && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" gutterBottom>
                          Selected Workers:
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                          {selectedWorkers.slice(0, 5).map((worker) => (
                            <Chip
                              key={worker.id}
                              label={worker.name}
                              size="small"
                              avatar={
                                <Avatar sx={{ 
                                  bgcolor: worker.transport_type === 'auto' ? 'primary.main' : 'secondary.main',
                                  width: 24, 
                                  height: 24 
                                }}>
                                  {worker.name.charAt(0)}
                                </Avatar>
                              }
                            />
                          ))}
                          {selectedWorkers.length > 5 && (
                            <Chip 
                              label={`+${selectedWorkers.length - 5} more`} 
                              size="small" 
                              variant="outlined" 
                            />
                          )}
                        </Stack>
                      </Box>
                    )}
                    {fieldState.error && (
                      <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                        {fieldState.error.message}
                      </Typography>
                    )}
                  </Box>
                  <WorkerPlanManager
                    open={workerManagerOpen}
                    onClose={() => setWorkerManagerOpen(false)}
                    workers={workers}
                    selectedWorkerIds={field.value || []}
                    onWorkersChange={field.onChange}
                    onPlanWorkersChange={handlePlanWorkersChange}
                    title={mode === 'create' ? "Select Workers for Plan" : "Edit Plan Workers"}
                    includeByDefault={mode === 'create'}
                    regions={regions}
                    settlements={settlements}
                    selectedRId={workerManagerRegionId}
                    selectedSId={workerManagerSettlementId}
                    onRegionChange={setWorkerManagerRegionId}
                    onSettlementChange={setWorkerManagerSettlementId}
                    planWorkers={plan?.plan_workers || []}
                    planId={plan?.id}
                    planMonth={plan?.month}
                  />
                </FormControl>
              )}
            />
          </Stack>

          {/* Editable Program Stores Table (Edit Mode Only) */}
          {mode === 'edit' && plan?.plan_program_stores && plan.plan_program_stores.length > 0 && (
            <Box mt={4}>
              <Typography variant="h6" gutterBottom>
                Program Stores ({plan.plan_program_stores.length})
              </Typography>
              <Box sx={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: 8 }}>Program</th>
                      <th style={{ textAlign: 'left', padding: 8 }}>Store</th>
                      <th style={{ textAlign: 'left', padding: 8 }}>Visit Time (min)</th>
                      <th style={{ textAlign: 'left', padding: 8 }}>Rate</th>
                      <th style={{ textAlign: 'left', padding: 8 }}>Visits Count</th>
                      <th style={{ textAlign: 'left', padding: 8 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plan.plan_program_stores.map((pps: any) => (
                      <EditableProgramStoreRow
                        key={pps.id}
                        planId={plan.id}
                        pps={pps}
                        // planMonth={plan.month}
                      />
                    ))}
                  </tbody>
                </table>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={isLoading}>
            {isLoading ? 
              (mode === 'create' ? 'Creating...' : 'Updating...') : 
              (mode === 'create' ? 'Create Plan' : 'Update Plan')
            }
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default PlanFormDialog; 